import type { ClientAuthDTO, HouseholdMemberDTO } from '@brainkey/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { actingClient, readStoredActingClientId } from '../lib/api/actingClient';
import { ApiError, setActingForbiddenHandler, setSessionExpiredHandler } from '../lib/api/client';
import { clientAuthApi, clientHouseholdApi } from '../lib/api/endpoints';
import { tokenStore } from '../lib/api/tokenStore';
import { PERSISTED_CACHE_KEY } from '../lib/persistedQueryClient';
import { queryClient } from '../lib/queryClient';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  client: ClientAuthDTO | null;
  /** Step 1 of OTP login — always resolves (the backend never reveals
   * whether `identifier` matched an invited client). Does not touch the
   * session; the caller advances to the code step unconditionally. Resolves
   * to a dev-only plaintext code when the server has `OTP_DEV_EXPOSE_CODE`
   * enabled (never in production), else `undefined`. */
  requestOtp: (identifier: string) => Promise<string | undefined>;
  /** Step 2 — the actual session-establishing call. */
  verifyOtp: (identifier: string, code: string) => Promise<void>;
  /** Re-fetches `/client/auth/me` and updates `client` in place — used after
   * onboarding submission, where the tokens are unaffected but the client
   * row (and its `onboardingCompletedAt`) has changed. */
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
  /** "Sign out of all devices" (CP-P4-T03/DEC-0039) — revokes every
   * refresh token for the account, then clears this session too. */
  logoutAll: () => Promise<void>;
  /** Relatives the guardian can switch into (coach-enabled links only).
   * Empty for everyone without a household — the switcher then never renders. */
  household: HouseholdMemberDTO[];
  /** The relative currently being viewed, or `null` for the guardian's own
   * profile. All data pages show this profile; account/billing stay the guardian's. */
  activeMember: HouseholdMemberDTO | null;
  /** Switch to a relative (`id`) or back to the guardian (`null`). Drops every
   * cached query so nothing from the previous profile can show up in the next. */
  switchClient: (id: string | null) => Promise<void>;
}

/** Drops all client-side data belonging to the profile being left. */
async function discardCachedProfileData(): Promise<void> {
  await queryClient.cancelQueries();
  queryClient.clear();
  try {
    window.localStorage.removeItem(PERSISTED_CACHE_KEY);
  } catch {
    /* storage unavailable — nothing persisted to drop */
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStore.hasSession() ? 'loading' : 'unauthenticated',
  );
  const [client, setClient] = useState<ClientAuthDTO | null>(null);
  const [household, setHousehold] = useState<HouseholdMemberDTO[]>([]);
  const [activeClientId, setActiveClientId] = useState<string | null>(null);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    actingClient.set(null);
    setActiveClientId(null);
    setHousehold([]);
    setClient(null);
    setStatus('unauthenticated');
    void queryClient.clear();
  }, []);

  // Let the API layer force a logout when a refresh ultimately fails.
  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  const switchClient = useCallback(async (id: string | null) => {
    const next = id === null || id === '' ? null : id;
    if (next === actingClient.get()) return;
    // Set before clearing so any refetch triggered by the clear already
    // carries the new profile's header.
    actingClient.set(next);
    await discardCachedProfileData();
    setActiveClientId(next);
  }, []);

  // The coach switched the viewed profile off (or unlinked it) mid-session:
  // fall back to the guardian's own profile and refresh the switcher.
  useEffect(() => {
    setActingForbiddenHandler(() => {
      void switchClient(null).then(() =>
        clientHouseholdApi
          .list()
          .then((res) => setHousehold(res.members))
          .catch(() => undefined),
      );
    });
    return () => setActingForbiddenHandler(null);
  }, [switchClient]);

  // Hydrate the client from an existing session on first load.
  useEffect(() => {
    if (!tokenStore.hasSession()) return;
    const controller = new AbortController();
    const storedActing = readStoredActingClientId();
    clientAuthApi
      .me(controller.signal)
      .then(async (res) => {
        // Validate the remembered profile against the live household BEFORE
        // rendering the app, so pages never fire requests for a profile the
        // guardian can no longer open. Without a remembered profile the
        // household loads in the background instead.
        const loadHousehold = clientHouseholdApi
          .list(controller.signal)
          .then((h) => h.members)
          .catch(() => [] as HouseholdMemberDTO[]);
        if (storedActing !== null) {
          const members = await loadHousehold;
          if (controller.signal.aborted) return;
          setHousehold(members);
          if (members.some((m) => m.clientId === storedActing)) {
            actingClient.set(storedActing);
            setActiveClientId(storedActing);
          } else {
            actingClient.set(null);
          }
        } else {
          void loadHousehold.then((members) => {
            if (!controller.signal.aborted) setHousehold(members);
          });
        }
        setClient(res.client);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.isAuthError) {
          clearSession();
        } else if (!controller.signal.aborted) {
          // Network/5xx: keep the (possibly valid) session but show login;
          // the token survives, so a retry can recover.
          setStatus('unauthenticated');
        }
      });
    return () => controller.abort();
  }, [clearSession]);

  const requestOtp = useCallback(async (identifier: string) => {
    const res = await clientAuthApi.requestOtp(identifier);
    return res.devCode;
  }, []);

  const verifyOtp = useCallback(async (identifier: string, code: string) => {
    const pair = await clientAuthApi.verifyOtp(identifier, code);
    tokenStore.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
    actingClient.set(null);
    setActiveClientId(null);
    setClient(pair.client);
    setStatus('authenticated');
    void clientHouseholdApi
      .list()
      .then((res) => setHousehold(res.members))
      .catch(() => undefined);
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await clientAuthApi.me();
    setClient(res.client);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    if (refreshToken) {
      // Best-effort server-side revocation; never block the UI on it.
      await clientAuthApi.logout(refreshToken).catch(() => undefined);
    }
    clearSession();
  }, [clearSession]);

  const logoutAll = useCallback(async () => {
    // Best-effort server-side revocation; never block the UI on it.
    await clientAuthApi.logoutAll().catch(() => undefined);
    clearSession();
  }, [clearSession]);

  const activeMember = useMemo(
    () => household.find((m) => m.clientId === activeClientId) ?? null,
    [household, activeClientId],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      client,
      requestOtp,
      verifyOtp,
      refreshMe,
      logout,
      logoutAll,
      household,
      activeMember,
      switchClient,
    }),
    [
      status,
      client,
      requestOtp,
      verifyOtp,
      refreshMe,
      logout,
      logoutAll,
      household,
      activeMember,
      switchClient,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
